import React, { useMemo, useState } from 'react';
import { Calculator, Leaf, Coins, Sparkles, Wifi } from 'lucide-react';
import SectionLabel from '../ui-shree/SectionLabel';

/**
 * ROIofPurity — a client-side calculator for hotel partners.
 *
 * Inputs:
 *   - rooms                       (count)
 *   - bottlesPerRoomPerDay        (typical hotel: 1.5–3 single-use bottles)
 *
 * Outputs (per year):
 *   1. Operational savings   — refill economics monitored by Savitri's
 *      Atmosic IoT (avoids stockouts, batches refills, cuts staff handling)
 *   2. ESG impact            — kg of plastic averted, in absolute terms
 *   3. Luxury 2030 score     — qualitative index combining
 *      Verifiability (Atmosic IoT) + Sovereignty (211M $SEVA wallet) +
 *      Provenance (on-chain anchor) into a single 0–100 reading
 *
 * Assumptions (kept explicit on the page — atelier honesty):
 *   - Disposable PET bottle    : 30 g plastic, $0.55 wholesale incl. ops
 *   - Refill cost equivalent   : $0.18 per dispense (5× reuse cycle)
 *   - Staff handling saved     : $0.08 per dispense (Atmosic auto-replenish)
 *   - Shreesavaa vessel        : 251 ml standard (the brand's binary truth)
 *   - Avg guest dispense       : 25 ml per use
 *   - Refill cycle             : (dispenses × 25) / 251 → IoT events / yr
 *   - Brand value uplift       : modelled, not banked — shown as an index
 */

const PET_GRAMS = 30;
const DISPOSABLE_COST = 0.55;
const REFILL_COST = 0.18;
const STAFF_SAVING_PER_DISPENSE = 0.08;
const VESSEL_CAPACITY_ML = 251;          // the binary truth — every Shreesavaa vessel
const AVG_DISPENSE_ML    = 25;           // typical amenity application volume
const PRODUCTION_UNIT_COST_USD = 8.30;   // the binary truth — production cost of one 251 ml vessel

const fmtUSD = (n) =>
  n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });

const fmtKg = (n) =>
  `${Math.round(n).toLocaleString('en-US')} kg`;

const ROIofPurity = ({ testId = 'roi-of-purity' }) => {
  const [rooms, setRooms] = useState(120);
  const [bottlesPerRoomPerDay, setBpRpD] = useState(2.4);

  const result = useMemo(() => {
    const dispensesPerYear = rooms * bottlesPerRoomPerDay * 365;
    const plasticKg = (dispensesPerYear * PET_GRAMS) / 1000;
    const opsSavings =
      dispensesPerYear * (DISPOSABLE_COST - REFILL_COST + STAFF_SAVING_PER_DISPENSE);
    // 251 ml vessel = higher refill velocity = more IoT events per year
    const refillCyclesPerYear = (dispensesPerYear * AVG_DISPENSE_ML) / VESSEL_CAPACITY_ML;
    // Luxury 2030 index — three weighted dimensions clamped 0–100.
    // Verifiability is now driven by IoT data velocity (refill cycles), not raw dispenses.
    const verifiability = Math.min(100, refillCyclesPerYear / 80);  // 8k refills/yr → 100
    const sovereignty   = Math.min(100, 30 + rooms / 4);            // base 30, +1/4 per room
    const provenance    = 92;                                       // fixed: on-chain anchor live
    const luxuryIndex = Math.round(
      verifiability * 0.4 + sovereignty * 0.3 + provenance * 0.3,
    );
    return {
      dispensesPerYear,
      refillCyclesPerYear,
      plasticKg,
      opsSavings,
      luxuryIndex,
      verifiability,
      sovereignty,
      provenance,
    };
  }, [rooms, bottlesPerRoomPerDay]);

  return (
    <section
      id="roi-of-purity"
      data-testid={testId}
      className="px-6 md:px-12 py-24 md:py-32 bg-[#F4F8F9]"
    >
      <div className="max-w-[1400px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 mb-14">
          <div className="md:col-span-7">
            <p
              data-testid="roi-frame-overline"
              className="font-mono text-[10px] tracking-[0.32em] uppercase text-[#90B0BF] mb-5"
            >
              <span className="inline-block w-7 h-px bg-[#90B0BF] mr-3 align-middle" />
              For the Owner-Operator · Long-term asset value
            </p>
            <SectionLabel testId="roi-label">
              ROI of Purity · the Owner-Operator audit
            </SectionLabel>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#7C9CB1] font-light leading-[1.05]">
              What does <em className="text-[#90B0BF]">witnessing</em>
              <br />
              return — over a decade?
            </h2>
            <p
              data-testid="roi-baseline-truth"
              className="mt-6 inline-flex items-center gap-3 font-mono text-[10px] md:text-[11px] tracking-[0.28em] uppercase text-[#7C9CB1] border border-[#D4AF5A]/60 bg-[#D4AF5A]/[0.08] px-4 py-2"
            >
              <span aria-hidden className="inline-block w-1.5 h-1.5 rounded-full bg-[#D4AF5A]" />
              Baseline truth · 251 ml vessel · ${PRODUCTION_UNIT_COST_USD.toFixed(2)} production unit cost
            </p>
          </div>
          <div className="md:col-span-5 md:pt-4">
            <p className="font-serif italic text-lg text-[#7C9CB1]/90 leading-relaxed max-w-md">
              An owner-operator's ledger, not a procurement quote. Operational
              savings from Atmosic-monitored refills, plastic averted from
              your property's footprint, and a quantified shift in guest
              trust and long-term asset value — calculated for the house
              you own, not the brand you license.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10 items-start">
          {/* Inputs */}
          <form
            data-testid="roi-form"
            onSubmit={(e) => e.preventDefault()}
            className="md:col-span-5 border border-[#C1D7E2] bg-[#F4F8F9] p-8 md:p-10 space-y-8"
          >
            <p className="flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] uppercase text-[#90B0BF]">
              <Calculator size={13} strokeWidth={1.5} /> Your property
            </p>

            {/* Rooms */}
            <label className="block">
              <span className="flex items-baseline justify-between font-mono text-[10px] tracking-[0.28em] uppercase text-[#90B0BF]">
                Rooms
                <span data-testid="roi-rooms-value" className="text-[12px] text-[#7C9CB1]">
                  {rooms}
                </span>
              </span>
              <input
                type="range"
                min="10"
                max="800"
                step="5"
                value={rooms}
                onChange={(e) => setRooms(Number(e.target.value))}
                data-testid="roi-rooms-input"
                className="mt-3 w-full accent-[#7C9CB1] cursor-pointer"
              />
              <span className="flex justify-between mt-1 font-mono text-[9px] tracking-[0.22em] text-[#90B0BF]/70">
                <span>10</span>
                <span>800</span>
              </span>
            </label>

            {/* Bottles per room per day */}
            <label className="block">
              <span className="flex items-baseline justify-between font-mono text-[10px] tracking-[0.28em] uppercase text-[#90B0BF]">
                Single-use bottles / room / day
                <span data-testid="roi-bottles-value" className="text-[12px] text-[#7C9CB1]">
                  {bottlesPerRoomPerDay.toFixed(1)}
                </span>
              </span>
              <input
                type="range"
                min="0.5"
                max="6"
                step="0.1"
                value={bottlesPerRoomPerDay}
                onChange={(e) => setBpRpD(Number(e.target.value))}
                data-testid="roi-bottles-input"
                className="mt-3 w-full accent-[#7C9CB1] cursor-pointer"
              />
              <span className="flex justify-between mt-1 font-mono text-[9px] tracking-[0.22em] text-[#90B0BF]/70">
                <span>0.5</span>
                <span>6.0</span>
              </span>
            </label>

            {/* Assumptions block — atelier honesty */}
            <details className="border-t border-[#C1D7E2] pt-5">
              <summary className="cursor-pointer font-mono text-[10px] tracking-[0.28em] uppercase text-[#90B0BF] hover:text-[#7C9CB1]">
                Assumptions
              </summary>
              <ul className="mt-3 space-y-2 font-mono text-[11px] text-[#7C9CB1]/90">
                <li>PET bottle weight · {PET_GRAMS} g</li>
                <li>Disposable cost · {fmtUSD(DISPOSABLE_COST)} / unit</li>
                <li>Refill cost · {fmtUSD(REFILL_COST)} / dispense</li>
                <li>Staff handling saved · {fmtUSD(STAFF_SAVING_PER_DISPENSE)} / dispense</li>
                <li className="font-medium">
                  Shreesavaa vessel · {VESSEL_CAPACITY_ML} ml
                  <span className="text-[#90B0BF]">  · binary truth</span>
                </li>
                <li className="font-medium">
                  Production unit cost · {fmtUSD(PRODUCTION_UNIT_COST_USD)} / {VESSEL_CAPACITY_ML} ml vessel
                  <span className="text-[#90B0BF]">  · baseline truth</span>
                </li>
                <li>Avg guest dispense · {AVG_DISPENSE_ML} ml</li>
                <li className="italic text-[#90B0BF]">
                  Brand value index modelled, not banked.
                </li>
              </ul>
            </details>
          </form>

          {/* Outputs */}
          <div className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {/* Operational savings */}
            <div
              data-testid="roi-output-savings"
              className="border border-[#C1D7E2] bg-[#F4F8F9] p-8"
            >
              <p className="flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] uppercase text-[#90B0BF]">
                <Coins size={13} strokeWidth={1.5} /> Operational savings
              </p>
              <p
                data-testid="roi-savings-value"
                className="mt-5 font-serif text-4xl md:text-5xl text-[#7C9CB1] font-light leading-none"
              >
                {fmtUSD(result.opsSavings)}
              </p>
              <p className="mt-2 font-mono text-[10px] tracking-[0.22em] uppercase text-[#90B0BF]">
                per year · Atmosic-monitored refills
              </p>
              <p
                data-testid="roi-savings-decade"
                className="mt-2 font-mono text-[10px] tracking-[0.22em] uppercase text-[#7C9CB1]"
              >
                <span className="text-[#90B0BF]">Over 10 years ·</span>{' '}
                <span className="text-[14px] tracking-[0.1em]">
                  {fmtUSD(result.opsSavings * 10)}
                </span>{' '}
                <span className="text-[#90B0BF]">retained by the owner-operator</span>
              </p>
              <p className="mt-4 text-[#7C9CB1]/85 font-light text-sm leading-relaxed">
                Refill economics + staff handling avoided across{' '}
                <span className="font-mono text-[12px]">{Math.round(result.dispensesPerYear).toLocaleString()}</span>{' '}
                witnessed dispenses.
              </p>
              <p
                data-testid="roi-refill-cycles"
                className="mt-3 font-mono text-[10px] tracking-[0.22em] uppercase text-[#7C9CB1]"
              >
                <span className="text-[#90B0BF]">Refill cycles · 251 ml vessel</span>{' '}
                <span className="text-[14px] tracking-[0.1em]">
                  {Math.round(result.refillCyclesPerYear).toLocaleString()}
                </span>{' '}
                <span className="text-[#90B0BF]">/ yr · Atmosic events</span>
              </p>
            </div>

            {/* ESG impact */}
            <div
              data-testid="roi-output-esg"
              className="border border-[#C1D7E2] bg-[#F4F8F9] p-8"
            >
              <p className="flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] uppercase text-[#90B0BF]">
                <Leaf size={13} strokeWidth={1.5} /> ESG · plastic averted
              </p>
              <p
                data-testid="roi-plastic-value"
                className="mt-5 font-serif text-4xl md:text-5xl text-[#7C9CB1] font-light leading-none"
              >
                {fmtKg(result.plasticKg)}
              </p>
              <p className="mt-2 font-mono text-[10px] tracking-[0.22em] uppercase text-[#90B0BF]">
                per year · PET removed from property footprint
              </p>
              <p className="mt-4 text-[#7C9CB1]/85 font-light text-sm leading-relaxed">
                A measurable line for your sustainability report — recorded,
                not estimated. (Honest framing: a Conscious Transition, not
                "zero plastic".)
              </p>
            </div>

            {/* Luxury 2030 index */}
            <div
              data-testid="roi-output-luxury"
              className="sm:col-span-2 border border-[#7C9CB1] bg-[#7C9CB1] text-[#F4F8F9] p-8"
            >
              <p className="flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] uppercase text-[#F4F8F9]/75">
                <Sparkles size={13} strokeWidth={1.5} /> Asset value · Owner-Operator index
              </p>
              <div className="mt-5 flex items-baseline justify-between gap-6 flex-wrap">
                <p
                  data-testid="roi-luxury-value"
                  className="font-serif text-5xl md:text-6xl font-light leading-none"
                >
                  {result.luxuryIndex}
                  <span className="font-mono text-[14px] tracking-[0.2em] text-[#F4F8F9]/70 align-baseline ml-2">
                    / 100
                  </span>
                </p>
                <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#F4F8F9]/80 max-w-xs">
                  Guest trust + Sovereignty + Provenance — the three
                  dimensions that compound into long-term asset value.
                </p>
              </div>

              {/* 3-bar breakdown */}
              <ul className="mt-7 space-y-3 font-mono text-[11px] text-[#F4F8F9]/90">
                {[
                  { l: 'Guest trust · witnessed dispenses', v: result.verifiability, icon: Wifi },
                  { l: 'Sovereignty · owner-operator',       v: result.sovereignty,   icon: Coins },
                  { l: 'Provenance · on-chain anchor',       v: result.provenance,    icon: Sparkles },
                ].map((row) => {
                  const RIcon = row.icon;
                  return (
                    <li key={row.l}>
                      <div className="flex items-center justify-between gap-4 mb-1">
                        <span className="flex items-center gap-2 tracking-[0.18em] uppercase text-[#F4F8F9]/80">
                          <RIcon size={11} strokeWidth={1.5} />
                          {row.l}
                        </span>
                        <span>{Math.round(row.v)}</span>
                      </div>
                      <span className="block h-px bg-[#F4F8F9]/25">
                        <span
                          className="block h-px bg-[#F4F8F9] transition-all duration-500"
                          style={{ width: `${row.v}%` }}
                        />
                      </span>
                    </li>
                  );
                })}
              </ul>

              {/* Concierge cross-reference */}
              <p
                data-testid="roi-concierges-strip"
                className="mt-7 pt-5 border-t border-[#F4F8F9]/25 font-mono text-[10px] tracking-[0.3em] uppercase text-[#F4F8F9]/75 leading-relaxed"
              >
                Concierges · <span className="text-[#F4F8F9]">Atreya</span> proposes
                the ritual, <span className="text-[#F4F8F9]">Savitri</span> arranges
                the refill, <span className="text-[#F4F8F9]">Indra</span> witnesses
                the count.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ROIofPurity;
